import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post } from 'aws-amplify/api';
import LoadingSpinner from './LoadingSpinner';

function DomainManagement({ domains, onDomainAdded, isAddMode = false }) {
  const navigate = useNavigate();
  const [domain, setDomain] = useState('');
  const [subdomainConfig, setSubdomainConfig] = useState({
    root: true,
    www: true,
    custom: []
  });
  const [customSubdomain, setCustomSubdomain] = useState('');
  const [accessConfig, setAccessConfig] = useState({
    allEvents: true,
    noEvents: false,
    specificEvents: []
  });
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(isAddMode);

  // Fetch user's events when component mounts
  React.useEffect(() => {
    fetchEvents();
  }, []);

  async function fetchEvents() {
    setIsLoading(true);
    try {
      const response = await get({
        apiName: 'events',
        path: '/events'
      });
      setEvents(response);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  }

  const handleAddCustomSubdomain = () => {
    if (customSubdomain && !subdomainConfig.custom.includes(customSubdomain)) {
      setSubdomainConfig({
        ...subdomainConfig,
        custom: [...subdomainConfig.custom, customSubdomain]
      });
      setCustomSubdomain('');
    }
  };

  const handleRemoveCustomSubdomain = (subdomain) => {
    setSubdomainConfig({
      ...subdomainConfig,
      custom: subdomainConfig.custom.filter(s => s !== subdomain)
    });
  };

  const handleToggleEventAccess = (eventId) => {
    if (accessConfig.allEvents || accessConfig.noEvents) {
      // If switching from allEvents or noEvents, start with an empty specificEvents list
      setAccessConfig({
        allEvents: false,
        noEvents: false,
        specificEvents: [eventId]
      });
    } else {
      // Toggle specific event in or out of the list
      const isSelected = accessConfig.specificEvents.includes(eventId);
      if (isSelected) {
        setAccessConfig({
          ...accessConfig,
          specificEvents: accessConfig.specificEvents.filter(id => id !== eventId)
        });
      } else {
        setAccessConfig({
          ...accessConfig,
          specificEvents: [...accessConfig.specificEvents, eventId]
        });
      }
    }
  };

  const handleAccessTypeChange = (type) => {
    if (type === 'all') {
      setAccessConfig({
        allEvents: true,
        noEvents: false,
        specificEvents: []
      });
    } else if (type === 'none') {
      setAccessConfig({
        allEvents: false,
        noEvents: true,
        specificEvents: []
      });
    } else {
      setAccessConfig({
        allEvents: false,
        noEvents: false,
        specificEvents: accessConfig.specificEvents
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!domain) {
      setError('Please enter a domain name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create a payload with domain configuration
      const payload = {
        domain: domain,
        subdomains: {
          root: subdomainConfig.root,
          www: subdomainConfig.www,
          custom: subdomainConfig.custom
        },
        access: {
          allEvents: accessConfig.allEvents,
          noEvents: accessConfig.noEvents,
          specificEvents: accessConfig.specificEvents
        }
      };

      // Send the domain configuration to the API
      await post({
        apiName: 'events',
        path: '/profile/domains', 
        body: payload
      });

      // Reset form and call callback
      if (onDomainAdded && typeof onDomainAdded === 'function') {
        onDomainAdded();
      }
      
      setDomain('');
      setSubdomainConfig({ root: true, www: true, custom: [] });
      setCustomSubdomain('');
      setAccessConfig({ allEvents: true, noEvents: false, specificEvents: [] });
      setShowAddForm(false);
      
      // If in add mode (separate page), navigate back to profile
      if (isAddMode) {
        navigate('/profile');
      }
    } catch (err) {
      console.error('Error adding domain:', err);
      setError('Failed to add domain. Please check your domain configuration and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderDomainsList = () => {
    if (!domains || !Array.isArray(domains) || domains.length === 0) {
      return (
        <p className="App-text">You haven&apos;t added any custom domains yet.</p>
      );
    }

    return (
      <div className="App-form-group">
        {domains.map((domain, index) => (
          <div key={index} className="App-card">
            <div className="App-actions">
              <div>
                <h4 className="App-heading">{domain.name}</h4>
                <div className="App-actions">
                  <span 
                    className={`App-status-indicator ${
                      domain.status === 'ACTIVE' ? 'App-status-active' : 
                      domain.status === 'PENDING' ? 'App-status-pending' : 'App-status-error'
                    }`}
                  ></span>
                  <span className="App-text">
                    {domain.status === 'ACTIVE' ? 'Active' : 
                     domain.status === 'PENDING' ? 'Pending Verification' : 'Configuration Required'}
                  </span>
                </div>
              </div>
              <div>
                {domain.status !== 'ACTIVE' && (
                  <button className="App-button App-button-secondary">
                    View Configuration
                  </button>
                )}
              </div>
            </div>
            
            <div className="App-margin-top">
              <p className="App-label"><strong>Subdomains:</strong></p>
              <div className="App-actions">
                {domain.subdomains.root && (
                  <span className="App-tag">{domain.name}</span>
                )}
                {domain.subdomains.www && (
                  <span className="App-tag">www.{domain.name}</span>
                )}
                {domain.subdomains.custom && domain.subdomains.custom.map((subdomain, idx) => (
                  <span key={idx} className="App-tag">{subdomain}.{domain.name}</span>
                ))}
              </div>
            </div>
            
            <div className="App-margin-top">
              <p className="App-label"><strong>Access:</strong> {
                domain.access.allEvents ? "All events" : 
                domain.access.noEvents ? "No events" : 
                "Specific events"
              }</p>
            </div>
            
            {domain.configRequirements && (
              <div className="App-margin-top App-info">
                <p className="App-label">Configuration Requirements:</p>
                <pre className="App-code">{domain.configRequirements}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderAddDomainForm = () => {
    if (isLoading && !events.length) {
      return <LoadingSpinner />;
    }

    return (
      <div className="card card--spaced">
        <h3 className="content__heading">Add New Domain</h3>
        {error && (
          <div className="form__error">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="form">
          <div className="form__group">
            <label className="form__label">Domain Name</label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="example.com"
              className="form__input"
              required
            />
          </div>
          
          <div className="form__group">
            <h4 className="form__heading">Subdomain Configuration</h4>
            <div className="form__group">
              <label className="form__label">
                <input
                  type="checkbox"
                  checked={subdomainConfig.root}
                  onChange={() => setSubdomainConfig({...subdomainConfig, root: !subdomainConfig.root})}
                  className="form__input--spacing"
                />
                Root domain (example.com)
              </label>
              <label className="form__label">
                <input
                  type="checkbox"
                  checked={subdomainConfig.www}
                  onChange={() => setSubdomainConfig({...subdomainConfig, www: !subdomainConfig.www})}
                  className="App-margin-right"
                />
                www subdomain (www.example.com)
              </label>
            </div>
            
            <div className="App-form-group">
              <h4 className="App-heading">Custom Subdomains</h4>
              <div className="App-actions">
                <input
                  type="text"
                  value={customSubdomain}
                  onChange={(e) => setCustomSubdomain(e.target.value)}
                  placeholder="events"
                  className="App-input"
                />
                <button
                  type="button"
                  onClick={handleAddCustomSubdomain}
                  className="App-button App-button-primary"
                >
                  Add
                </button>
              </div>
              {subdomainConfig.custom.length > 0 && (
                <div className="App-margin-top">
                  <h5 className="App-label">Added Custom Subdomains:</h5>
                  <div className="App-actions">
                    {subdomainConfig.custom.map((subdomain, index) => (
                      <div key={index} className="App-card">
                        <span>{subdomain}.{domain}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCustomSubdomain(subdomain)}
                          className="App-button App-button-danger App-margin-left"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="form__group">
            <h4 className="form__heading">Access Configuration</h4>
            <div className="form__group">
              <label className="form__label">
                <input
                  type="radio"
                  checked={accessConfig.allEvents}
                  onChange={() => handleAccessTypeChange('all')}
                  className="form__input--spacing"
                />
                Allow access to all events
              </label>
              <label className="form__label">
                <input
                  type="radio"
                  checked={accessConfig.noEvents}
                  onChange={() => handleAccessTypeChange('none')}
                  className="form__input--spacing"
                />
                Don&apos;t allow access to any events
              </label>
              <label className="form__label">
                <input
                  type="radio"
                  checked={!accessConfig.allEvents && !accessConfig.noEvents}
                  onChange={() => handleAccessTypeChange('specific')}
                  className="App-margin-right"
                />
                Allow access to specific events
              </label>
            </div>
            
            {!accessConfig.allEvents && !accessConfig.noEvents && events.length > 0 && (
              <div className="content--spaced">
                <h5 className="form__label">Select events:</h5>
                <div className="content__scrollable">
                  {events.map((event, index) => (
                    <label key={index} className="form__label">
                      <input
                        type="checkbox"
                        checked={accessConfig.specificEvents.includes(event.id)}
                        onChange={() => handleToggleEventAccess(event.id)}
                        className="App-margin-right"
                      />
                      {event.title}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="form__actions">
            {!isAddMode && (
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="button button--neutral"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="button button--primary"
            >
              {isLoading ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
        </form>
      </div>
    );
  };

  return (
    <div>
      <div className="App-actions">
        <h3 className="App-heading">Custom Domains</h3>
        {!showAddForm && !isAddMode && (
          <button 
            onClick={() => setShowAddForm(true)}
            className="App-button App-button-secondary"
          >
            Add Domain
          </button>
        )}
      </div>
      
      {showAddForm || isAddMode ? renderAddDomainForm() : renderDomainsList()}
    </div>
  );
}

export default DomainManagement;